import React, { useEffect } from "react";

// Lazy-loads Poppins once per page if a widget asks for it. BMC's
// official button uses Poppins, so we default to it.
const LOADED_FONTS = new Set();
function ensurePoppins() {
  if (LOADED_FONTS.has("Poppins")) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap";
  document.head.appendChild(link);
  LOADED_FONTS.add("Poppins");
}

/**
 * Inline Buy-Me-a-Coffee button styled identically to the floating
 * `button.prod.min.js` widget that BMC injects from its snippet, but
 * rendered as a normal anchor card so it lives inside the right-column
 * widget stack instead of floating fixed to the corner.
 *
 * The original snippet's config:
 *   slug, color, emoji, font, text, outline-color, font-color, coffee-color
 *
 * `coffee_color` paints the disc behind the emoji to match the official
 * widget when the admin picks a non-yellow background.
 */
export default function BMCButtonCard({ config }) {
  const slug = (config && config.slug) || "";
  const bg = (config && config.color) || "#FFDD00";
  const text = (config && config.text) || "Buy me a coffee";
  const emoji = (config && config.emoji) || "☕";
  const font = (config && config.font) || "Poppins";
  const outline = (config && config.outline_color) || "#000000";
  const fontColor = (config && config.font_color) || "#000000";
  const coffeeColor = (config && config.coffee_color) || "#FFFFFF";

  useEffect(() => {
    if (font === "Poppins") ensurePoppins();
  }, [font]);

  if (!slug) return null;

  const href = `https://www.buymeacoffee.com/${encodeURIComponent(slug)}`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="home-bmc-button"
      className="card-hover group flex items-center justify-center gap-3 rounded-md px-4 py-3 transition-transform hover:scale-[1.015] focus:outline-none focus:ring-2 focus:ring-[#0A0F17]"
      style={{
        backgroundColor: bg,
        color: fontColor,
        border: `1px solid ${outline}`,
        fontFamily: font === "Poppins" ? '"Poppins", system-ui, sans-serif' : `"${font}", system-ui, sans-serif`,
        fontWeight: 600,
      }}
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-7 items-center justify-center rounded-full text-base"
        style={{ backgroundColor: coffeeColor }}
      >
        {emoji}
      </span>
      <span className="text-base">{text}</span>
    </a>
  );
}
